import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, ApiError } from "@/lib/apiClient";

export type PortalRole = "customer" | "staff";

export type PortalUser = {
  username?: string | null;
  displayName?: string | null;
  email?: string | null;
  auth_provider?: string | null;
  portal_role?: PortalRole | string | null;
  acting_as_email?: string | null;
  effective_email?: string | null;
  ansprechpartner_ids?: number[];
};

type AuthState = {
  user: PortalUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  impersonate: (email: string) => Promise<void>;
  clearImpersonation: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<PortalUser>("/auth/portal/me");
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/portal/logout");
    } catch {
      // Cookie lokal trotzdem verwerfen
    }
    setUser(null);
  }, []);

  const impersonate = useCallback(async (email: string) => {
    const res = await api.post<{ ok: boolean; user: PortalUser }>(
      "/auth/portal/impersonate",
      { email: email.trim() },
    );
    if (res?.user) {
      setUser(res.user);
    } else {
      await refresh();
    }
  }, [refresh]);

  const clearImpersonation = useCallback(async () => {
    const res = await api.post<{ ok: boolean; user: PortalUser }>(
      "/auth/portal/impersonate/clear",
    );
    if (res?.user) {
      setUser(res.user);
    } else {
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      user,
      loading,
      refresh,
      logout,
      impersonate,
      clearImpersonation,
    }),
    [user, loading, refresh, logout, impersonate, clearImpersonation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function usePortalAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("usePortalAuth außerhalb von PortalAuthProvider");
  }
  return ctx;
}

export function RequirePortalAuth({ children }: { children: ReactNode }) {
  const { user, loading } = usePortalAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted">
        Sitzung wird geprüft …
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
